// Who has clinched a playoff seat and who is out of the race (H1, B-397):
// the "x" and the "e" a table carries on ESPN, Yahoo and Sleeper, worked out
// from the records and the games each team has left.
//
// CERTAIN, NEVER LIKELY. A team is clinched only when no run of the games
// left can put it outside the field, and out only when none can put it in;
// everything between is alive. The tiebreakers are not run forward (a
// points-for tiebreak depends on scores nobody has played), so a team level
// with the line counts as a threat both ways, and two teams who still play
// each other are each allowed to win: the marks can come a week later than
// an exact search would put them, never a week early.
//
// MEASURED AS THE TABLE RANKS, by the share of games won, a tie half a win:
// a team's final share lies between every game left lost and every game left
// won. Counting wins alone agrees only while every team has played as many
// games, and a league of an odd size has byes (the review of 2026-10-17: a
// 5–2–1 team with its bye taken finishes above a 6–2 team that loses its
// last game). A median league's week is two results, and the caller counts
// both.
//
// ONLY WHERE A RECORD DECIDES THE SEATS: a race on points (total points,
// elimination) and an all-play table, whose weeks move a record by the
// league's size, are not measured. Null for every team there.
//
// DIVISION WINNERS FIRST (`seedPlayoffField`) is measured since 2026-10-20
// (PLAN-2026-10-19-what-has-to-happen §6): a team behind the field can still
// win its division and take a seed above everyone who did not, so each other
// division still open counts as one more team that could be seeded ahead.
//
// ON THE WIRE (CLAUDE.md, the backend is a product): `StandingsRow.playoffStatus`
// and the playoff picture's seeds, which a second frontend would otherwise
// have to work out from the schedule.

import { seedPlayoffField } from './bracket';

export type PlayoffStatus = 'clinched' | 'alive' | 'eliminated';

export interface StatusTeam {
  teamId: string;
  wins: number;
  losses: number;
  ties: number;
  /** Results still to come in the regular season: games against somebody, and median games. */
  remaining: number;
  /** The table's place, for a season with nothing left to play. */
  rank: number;
  /** `teams.division_id`; read only when the league seeds division winners first. */
  divisionId?: string | null;
}

/** The share of games won, a tie half, from a record; 0 with no games, as the table counts it. */
function share(won: number, games: number): number {
  return games === 0 ? 0 : won / games;
}

/**
 * Each team's status in the race for the playoffs, or null where it is not
 * measured (see above): no seats, a seat for everybody, or a format that does
 * not rank by record.
 */
export function playoffStatuses(
  teams: readonly StatusTeam[],
  playoffs: { teamCount: number; seeding: 'standings' | 'division-winners-first' },
  format: { ranksByRecord: boolean },
  /** Every regular-season game is final: the table is the season's. */
  seasonOver: boolean,
): Map<string, PlayoffStatus> | null {
  const seats = playoffs.teamCount;
  if (!format.ranksByRecord) return null;
  if (seats <= 0 || seats >= teams.length) return null;
  const divisionOf = (t: StatusTeam) => t.divisionId ?? null;
  // A league seeded by division with no divisions is seeded by the table, as
  // `seedPlayoffField` seeds it.
  const divided =
    playoffs.seeding === 'division-winners-first' && teams.some((t) => divisionOf(t) !== null);
  const out = new Map<string, PlayoffStatus>();
  // THE REGULAR SEASON OVER: the field is the table's, its tiebreakers
  // already run, with division winners promoted where they are.
  if (seasonOver) {
    const field = new Set(
      seedPlayoffField(
        teams.map((t) => ({ teamId: t.teamId, rank: t.rank, divisionId: divisionOf(t) })),
        playoffs,
      ).map((s) => s.teamId),
    );
    for (const t of teams) out.set(t.teamId, field.has(t.teamId) ? 'clinched' : 'eliminated');
    return out;
  }
  // WEEKS LEFT AND NO GAMES IN THEM is a schedule not written, not a race
  // decided: nothing to measure from.
  if (teams.every((t) => t.remaining === 0)) return null;
  const won = (t: StatusTeam) => t.wins + t.ties / 2;
  const games = (t: StatusTeam) => t.wins + t.losses + t.ties + t.remaining;
  /** Every game left lost. */
  const worst = (t: StatusTeam) => share(won(t), games(t));
  /** Every game left won. */
  const best = (t: StatusTeam) => share(won(t) + t.remaining, games(t));
  const divisions = new Set(teams.map(divisionOf).filter((d): d is string => d !== null));
  for (const t of teams) {
    const others = teams.filter((o) => o.teamId !== t.teamId);
    // Teams that could still finish level with this one's worst or above it.
    const threat = (o: StatusTeam) => best(o) >= worst(t);
    // Teams sure to finish above everything this one could still reach.
    const sure = (o: StatusTeam) => worst(o) > best(t);
    if (!divided) {
      const threats = others.filter(threat).length;
      const beyond = others.filter(sure).length;
      out.set(t.teamId, threats < seats ? 'clinched' : beyond >= seats ? 'eliminated' : 'alive');
      continue;
    }
    const mine = divisionOf(t);
    const otherDivisions = [...divisions].filter((d) => d !== mine);
    const inDivision = (d: string, which: (o: StatusTeam) => boolean) =>
      others.some((o) => divisionOf(o) === d && which(o));
    // CLINCHED: every threat, and one more for each other division a team
    // that is not a threat could still win, seeded above as its winner.
    const couldBeAhead =
      others.filter(threat).length +
      otherDivisions.filter((d) => inDivision(d, (o) => !threat(o))).length;
    // OUT: winning its division where it still can, each other division
    // with a team sure to finish above it has a winner seeded above; where
    // it cannot, every division's winner is, and every team sure to finish
    // above it, each counted once.
    const canWin = mine !== null && !inDivision(mine, sure);
    const sureAhead = canWin
      ? otherDivisions.filter((d) => inDivision(d, sure)).length
      : others.filter(sure).length + [...divisions].filter((d) => !inDivision(d, sure)).length;
    out.set(
      t.teamId,
      couldBeAhead < seats ? 'clinched' : sureAhead >= seats ? 'eliminated' : 'alive',
    );
  }
  return out;
}
